// ISDP CUSTOM: Entire file — System entity "Documentation" tab
// Queries catalog for all components in the system that have backstage.io/techdocs-ref annotation
// and renders links to their TechDocs pages
import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { Link } from 'react-router-dom';
import { docsPath, entityPath } from './entityHelpers';
import './SystemDocsCard.css';

export function SystemDocsCard() {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [docEntities, setDocEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  const systemName = entity.metadata.name;

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: {
          'spec.system': systemName,
          'metadata.annotations.backstage.io/techdocs-ref':
            CATALOG_FILTER_EXISTS,
        },
      })
      .then(response => {
        setDocEntities(response.items);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [catalogApi, systemName]);

  if (loading) {
    return (
      <div className="devhub-system-docs-card">
        <div className="devhub-system-docs-card__header">
          System Documentation
        </div>
        <div className="devhub-system-docs-card__body">
          <div className="devhub-system-docs-card__loading">
            <div className="devhub-system-docs-card__spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (docEntities.length === 0) {
    return (
      <div className="devhub-system-docs-card">
        <div className="devhub-system-docs-card__header">
          System Documentation
        </div>
        <div className="devhub-system-docs-card__body">
          <p className="devhub-system-docs-card__empty-text">
            No components with TechDocs found in this system. Add a{' '}
            <code>backstage.io/techdocs-ref</code> annotation to component
            entities with <code>spec.system: {systemName}</code> to see their
            documentation here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="devhub-system-docs-grid">
      {docEntities.map(docEntity => {
        const name = docEntity.metadata.name;
        const namespace = docEntity.metadata.namespace || 'default';
        const kind = docEntity.kind.toLowerCase();
        const title = docEntity.metadata.title || name;
        const description =
          docEntity.metadata.description || `${docEntity.kind} documentation`;

        return (
          <div className="devhub-system-docs-card" key={`${kind}:${namespace}/${name}`}>
            <div className="devhub-system-docs-card__header">
              {title}
              <div className="devhub-system-docs-card__subheader">
                <odx-chip variant="neutral">
                  {docEntity.kind}
                </odx-chip>
                <span className="devhub-system-docs-card__namespace">
                  {namespace}/{name}
                </span>
              </div>
            </div>
            <hr className="devhub-system-docs-card__divider" />
            <div className="devhub-system-docs-card__body">
              <p className="devhub-system-docs-card__description">
                {description}
              </p>
              <ul className="devhub-system-docs-card__link-list">
                <li className="devhub-system-docs-card__link-item">
                  <Link to={docsPath(docEntity)}>
                    <span className="devhub-system-docs-card__link-icon">
                      <odx-icon name="core::document" size="sm" />
                    </span>
                    <span className="devhub-system-docs-card__link-text">
                      Open TechDocs
                    </span>
                    <span className="devhub-system-docs-card__link-chevron">
                      <odx-icon name="core::chevron-right" size="sm" />
                    </span>
                  </Link>
                </li>
                <li className="devhub-system-docs-card__link-item">
                  <Link to={entityPath(docEntity)}>
                    <span className="devhub-system-docs-card__link-icon--muted">
                      <odx-icon name="core::chevron-right" size="sm" />
                    </span>
                    <span className="devhub-system-docs-card__link-text">
                      View entity
                    </span>
                    <span className="devhub-system-docs-card__link-chevron">
                      <odx-icon name="core::chevron-right" size="sm" />
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}
